import React, { FormEvent, useCallback, useState } from "react";
import {
  ClinicianPortalApiError,
  clinicianPortalApi,
} from "../api/clinicianPortalApi";
import { AdminTherapistRole } from "../api/contracts";
import { ClinicianRoleSelect } from "../components/ClinicianRoleSelect";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { PortalLink } from "../components/PortalLink";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";

interface FormErrors {
  therapistName?: string;
  email?: string;
  role?: string;
  clinics?: string;
}

function safeApiMessage(error: ClinicianPortalApiError, fallback: string) {
  return error.message.trim() || fallback;
}

function createErrorMessage(error: unknown): FormErrors & { form?: string } {
  if (!(error instanceof ClinicianPortalApiError)) {
    return { form: "Therapist could not be created. Try again." };
  }

  switch (error.code) {
    case "DUPLICATE_THERAPIST":
      return { email: "A therapist with this email already exists." };
    case "INVALID_THERAPIST_NAME":
      return {
        therapistName: safeApiMessage(error, "Enter a valid therapist name."),
      };
    case "INVALID_EMAIL":
      return { email: safeApiMessage(error, "Enter a valid email address.") };
    case "INVALID_ROLE":
      return { role: safeApiMessage(error, "Select a valid role.") };
    case "CLINIC_NOT_FOUND":
      return {
        clinics: safeApiMessage(
          error,
          "One or more selected clinics are no longer available. Refresh and try again.",
        ),
      };
    case "INVALID_REQUEST":
      return {
        form: safeApiMessage(
          error,
          "Review the required fields and try again.",
        ),
      };
    case "NOT_AUTHORIZED":
    case "NOT_FOUND":
      return { form: "This portal record is unavailable." };
    default:
      return { form: "Therapist could not be created. Try again." };
  }
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function AddTherapistPage({ clinicianCode }: { clinicianCode: string }) {
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getClinics(clinicianCode, signal),
    [clinicianCode],
  );
  const clinicState = usePortalData(request);
  const mutation = usePortalMutation();
  const [therapistName, setTherapistName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AdminTherapistRole>("therapist");
  const [advancedDashboardEnabled, setAdvancedDashboardEnabled] =
    useState(true);
  const [selectedClinicIds, setSelectedClinicIds] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<FormErrors>({});
  const data =
    clinicState.status === "success" || clinicState.status === "refreshing"
      ? clinicState.data
      : undefined;
  const available = Boolean(
    data?.found &&
      data.authorized &&
      data.therapist?.role === "admin" &&
      data.patient === null,
  );
  const isSubmitting = mutation.state.status === "pending";
  const apiErrors =
    mutation.state.status === "error"
      ? createErrorMessage(mutation.state.error)
      : {};
  const errors = { ...validationErrors, ...apiErrors };

  const clearErrors = () => {
    setValidationErrors({});
    mutation.reset();
  };

  const toggleClinic = (clinicId: string) => {
    clearErrors();
    setSelectedClinicIds((current) =>
      current.includes(clinicId)
        ? current.filter((id) => id !== clinicId)
        : [...current, clinicId],
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const normalizedName = therapistName.trim();
    const normalizedEmail = email.trim().toLowerCase();
    const nextErrors: FormErrors = {};
    if (!normalizedName) {
      nextErrors.therapistName = "Therapist name is required.";
    }
    if (!normalizedEmail) {
      nextErrors.email = "Email is required.";
    } else if (!isValidEmail(normalizedEmail)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (role !== "therapist" && role !== "supervisor" && role !== "admin") {
      nextErrors.role = "Select a valid role.";
    }
    if (!selectedClinicIds.length) {
      nextErrors.clinics = "Select at least one clinic.";
    }
    setValidationErrors(nextErrors);
    mutation.reset();
    if (Object.keys(nextErrors).length) return;

    const result = await mutation.run("create-therapist", (signal) =>
      clinicianPortalApi.createTherapist(
        clinicianCode,
        normalizedName,
        normalizedEmail,
        role,
        selectedClinicIds,
        advancedDashboardEnabled,
        signal,
      ),
    );
    if (result?.created) {
      navigateWithinPortal(
        withClinicianCode("/clinician-portal/admin/therapists", clinicianCode),
      );
    }
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey="therapists"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Add Therapist"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "Administration" },
              {
                label: "Therapists",
                path: "/clinician-portal/admin/therapists",
              },
              { label: "Add Therapist" },
            ]}
          />
        }
      />
      <div className="cp-page-content">
        {clinicState.status === "loading" ? (
          <LoadingState label="Loading therapist form" />
        ) : null}
        {clinicState.status === "error" ? (
          <ErrorState error={clinicState.error} onRetry={clinicState.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Therapist creation is unavailable")} />
        ) : null}
        {available && data ? (
          <section className="cp-record-form" aria-label="Add Therapist form">
            <div className="cp-record-form__header">
              <p>Create a clinician account and assign at least one clinic.</p>
            </div>
            <form onSubmit={handleSubmit} noValidate>
              <div className="cp-form-field">
                <label htmlFor="therapist-name">
                  Therapist Name <span aria-hidden="true">*</span>
                </label>
                <input
                  id="therapist-name"
                  className="cp-form-input"
                  type="text"
                  value={therapistName}
                  required
                  aria-invalid={Boolean(errors.therapistName)}
                  aria-describedby={
                    errors.therapistName ? "therapist-name-error" : undefined
                  }
                  onChange={(event) => {
                    clearErrors();
                    setTherapistName(event.target.value);
                  }}
                />
                {errors.therapistName ? (
                  <p className="cp-form-error" id="therapist-name-error">
                    {errors.therapistName}
                  </p>
                ) : null}
              </div>

              <div className="cp-form-field">
                <label htmlFor="therapist-email">
                  Email <span aria-hidden="true">*</span>
                </label>
                <input
                  id="therapist-email"
                  className="cp-form-input"
                  type="email"
                  value={email}
                  required
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={
                    errors.email ? "therapist-email-error" : undefined
                  }
                  onChange={(event) => {
                    clearErrors();
                    setEmail(event.target.value);
                  }}
                />
                {errors.email ? (
                  <p className="cp-form-error" id="therapist-email-error">
                    {errors.email}
                  </p>
                ) : null}
              </div>

              <div className="cp-form-field">
                <label htmlFor="therapist-role">
                  Role <span aria-hidden="true">*</span>
                </label>
                <ClinicianRoleSelect
                  id="therapist-role"
                  value={role}
                  invalid={Boolean(errors.role)}
                  ariaDescribedBy={
                    errors.role ? "therapist-role-error" : undefined
                  }
                  disabled={isSubmitting}
                  onChange={(nextRole) => {
                    clearErrors();
                    setRole(nextRole);
                  }}
                />
                {errors.role ? (
                  <p className="cp-form-error" id="therapist-role-error">
                    {errors.role}
                  </p>
                ) : null}
              </div>

              <div className="cp-form-field">
                <span className="cp-lov-checkbox-label">Homepage</span>
                <label
                  className="cp-form-checkbox"
                  htmlFor="therapist-advanced-dashboard"
                >
                  <input
                    id="therapist-advanced-dashboard"
                    type="checkbox"
                    checked={advancedDashboardEnabled}
                    disabled={isSubmitting}
                    aria-describedby="therapist-advanced-dashboard-help"
                    onChange={(event) => {
                      clearErrors();
                      setAdvancedDashboardEnabled(event.target.checked);
                    }}
                  />
                  Advanced Dashboard
                </label>
                <p
                  className="cp-form-help"
                  id="therapist-advanced-dashboard-help"
                >
                  Show Safety Events and Caseload Engagement cards on the
                  clinician homepage.
                </p>
              </div>

              <fieldset
                className="cp-form-field cp-form-fieldset"
                aria-describedby={errors.clinics ? "clinics-error" : undefined}
                aria-invalid={Boolean(errors.clinics)}
                aria-required="true"
              >
                <legend>
                  Clinics <span aria-hidden="true">*</span>
                </legend>
                {data.clinics.length ? (
                  <div className="cp-checkbox-list">
                    {data.clinics.map((clinic) => (
                      <label className="cp-checkbox-option" key={clinic.id}>
                        <input
                          type="checkbox"
                          value={clinic.id}
                          checked={selectedClinicIds.includes(clinic.id)}
                          onChange={() => toggleClinic(clinic.id)}
                        />
                        <span>
                          <strong>{clinic.clinicName || "—"}</strong>
                          <small>{clinic.status || "—"}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="cp-form-empty">
                    No clinics are currently available. Create a clinic before
                    adding a therapist.
                  </p>
                )}
                {errors.clinics ? (
                  <p className="cp-form-error" id="clinics-error">
                    {errors.clinics}
                  </p>
                ) : null}
              </fieldset>

              {apiErrors.form ? (
                <p className="cp-form-error cp-form-error--panel" role="alert">
                  {apiErrors.form}
                </p>
              ) : null}

              <div className="cp-form-actions cp-form-actions--sticky">
                <PortalLink
                  className="cp-button cp-button--secondary"
                  path="/clinician-portal/admin/therapists"
                  clinicianCode={clinicianCode}
                >
                  Cancel
                </PortalLink>
                <button
                  type="submit"
                  className="cp-button cp-button--primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating…" : "Create Therapist"}
                </button>
              </div>
            </form>
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
